import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, Flex } from '@chakra-ui/react';

import FormInput from '../../../../components/form/FormInput';
import { inputTypes } from '../../../../components/form/FormInput/util';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { getValidationObject } from '../../../../constants/regexPatterns';
import { selectUser, selectUserSettings, updateUserSettings } from '../../../../redux/modules/user';
import { createMap } from '../../../../utils/arrayUtil';
import SaveButton from '../../components/SaveButton';
import TabPanelBox from '../../components/TabPanelBox';
import TabPanelFooter from '../../components/TabPanelFooter';

import AutoApprovalEmailInputs from './AutoApprovalEmailInputs';
import SettingInfo from './SettingInfo';
import { EmailSettingTypes, getSettingDetails } from './util';

function EmailTabPanel(props) {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const userSettings = useSelector(selectUserSettings);

  const settingsMap = useMemo(() => createMap(userSettings, 'name'), [userSettings]);

  const replyToSetting = settingsMap[EmailSettingTypes.ReplyTo];
  const autoApprovalSetting = settingsMap[EmailSettingTypes.AutoApproval];

  const useFormMethods = useForm({
    defaultValues: {
      replyToEmail: replyToSetting?.value,
      replyToEnabled: replyToSetting?.isEnabled,
      autoApproval: autoApprovalSetting?.value,
      autoApprovalEnabled: autoApprovalSetting?.isEnabled
    }
  });

  const { register, handleSubmit, watch, setValue, formState } = useFormMethods;
  const { isSubmitting } = formState;

  const onSubmit = async data => {
    await dispatch(updateUserSettings(replyToSetting.id, {
      isEnabled: data.replyToEnabled,
      value: data.replyToEmail
    }));
  };

  return (
    <TabPanelBox {...props}>
      <form id="emailSettings" onSubmit={handleSubmit(onSubmit)}>
        <Box
          data-analytics-id={analyticsIds.userSettings.emailSettings.replyToSwitch}
          mb={3}
          px={5}
          py={4}
          textAlign="left"
          w="100%"
        >
          <SettingInfo
            description={getSettingDetails(EmailSettingTypes.ReplyTo).description}
            name="replyToEnabled"
            title={getSettingDetails(EmailSettingTypes.ReplyTo).title}
            watch={watch}
            onChange={value => setValue('replyToEnabled', value)}
          />
          {
            watch('replyToEnabled') && (
              <Flex direction="column" mt={4} width="60%">
                <FormInput
                  dataAnalyticsId="replyToEmail"
                  inputName="replyToEmail"
                  labelName="Email"
                  placeholder="email address"
                  type={inputTypes.standardSmall}
                  useFormMethods={useFormMethods}
                  validationRules={{
                    required: 'Email address is required',
                    ...getValidationObject('commaSeparatedEmailAddress')
                  }}
                />
              </Flex>
            )
          }
        </Box>
        <Divider my={3} />
        <Box
          data-analytics-id={analyticsIds.userSettings.emailSettings.automaticInsuranceApprovalEmailSwitch}
          px={5}
          py={4}
          textAlign="left"
          w="100%"
        >
          <SettingInfo
            isDisabled
            description={getSettingDetails(EmailSettingTypes.AutoApproval).description}
            name="autoApprovalEnabled"
            title={getSettingDetails(EmailSettingTypes.AutoApproval).title}
            watch={watch}
            onChange={value => setValue('autoApprovalEnabled', value)}
          />
          {
            watch('autoApprovalEnabled') && (
              <AutoApprovalEmailInputs
                register={register}
                userEmail={user.userEmail}
              />
            )
          }
        </Box>
        <Divider my={3} />
        <TabPanelFooter>
          <SaveButton
            dataAnalyticsId={analyticsIds.userSettings.emailSettings.saveSettingsButton}
            dusk={analyticsIds.userSettings.emailSettings.saveSettingsButton}
            formId="emailSettings"
            isDisabled={isSubmitting}
          />
        </TabPanelFooter>
      </form>
    </TabPanelBox>
  );
}

export default EmailTabPanel;
